import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { adminAPI, AdminGroup, AdminGroupMember, AdminGroupMemberMacroSignal } from '../api';
import { useAuth } from '../contexts/AuthContext';
import { formatDateTime, formatShortDate, getTimestampValue } from '../utils/dateUtils';

type GroupDetailSort = 'last_active' | 'logging' | 'on_target' | 'meals' | 'email';

const macroSignals: Array<{ key: 'carbs' | 'protein' | 'fat'; label: string }> = [
  { key: 'carbs', label: 'C' },
  { key: 'protein', label: 'P' },
  { key: 'fat', label: 'F' },
];

const sortOptions: Array<{ value: GroupDetailSort; label: string }> = [
  { value: 'last_active', label: 'Recently active' },
  { value: 'logging', label: 'Best logging' },
  { value: 'on_target', label: 'Most on target' },
  { value: 'meals', label: 'Most meals' },
  { value: 'email', label: 'Email' },
];

const getLastActiveTimestamp = (member: Pick<AdminGroupMember, 'last_login' | 'last_meal_logged'>): number => {
  return Math.max(getTimestampValue(member.last_login), getTimestampValue(member.last_meal_logged));
};

const getLastActiveValue = (member: Pick<AdminGroupMember, 'last_login' | 'last_meal_logged'>): string | null => {
  if (getTimestampValue(member.last_login) >= getTimestampValue(member.last_meal_logged)) {
    return member.last_login;
  }
  return member.last_meal_logged;
};

const getLoggingTone = (daysLogged: number): string => {
  if (daysLogged >= 6) return 'text-green-600';
  if (daysLogged >= 3) return 'text-amber-600';
  return 'text-red-600';
};

const hasCompleteTargets = (member: Pick<AdminGroupMember, 'target_carbs' | 'target_protein' | 'target_fat'>): boolean => {
  return member.target_carbs != null && member.target_protein != null && member.target_fat != null;
};

const getHitRateTone = (hitRatePct: number | null): string => {
  if (hitRatePct == null) return 'text-gray-400';
  if (hitRatePct >= 70) return 'text-green-600';
  if (hitRatePct >= 40) return 'text-amber-600';
  return 'text-red-600';
};

const getMacroSignalClasses = (signal: AdminGroupMemberMacroSignal): string => {
  if (signal.direction === 'no_log' || signal.direction === 'missing_target' || signal.delta_pct == null) {
    return 'bg-gray-100 text-gray-500';
  }

  const absoluteDelta = Math.abs(signal.delta_pct);
  if (absoluteDelta <= 10) {
    return 'bg-green-100 text-green-700';
  }
  if (absoluteDelta <= 25) {
    return 'bg-amber-100 text-amber-700';
  }
  return 'bg-red-100 text-red-700';
};

const formatMacroSignal = (signal: AdminGroupMemberMacroSignal): string => {
  if (signal.direction === 'no_log' || signal.delta_pct == null) {
    return 'No log';
  }

  if (signal.direction === 'missing_target') {
    return 'No target';
  }

  return `${signal.delta_pct > 0 ? '+' : ''}${signal.delta_pct}%`;
};

const sortMembers = (members: AdminGroupMember[], sortBy: GroupDetailSort): AdminGroupMember[] => {
  const nextMembers = [...members];

  nextMembers.sort((left, right) => {
    if (sortBy === 'email') {
      return left.email.localeCompare(right.email);
    }

    if (sortBy === 'last_active') {
      return getLastActiveTimestamp(right) - getLastActiveTimestamp(left);
    }

    if (sortBy === 'logging') {
      if (right.days_logged_last_7 !== left.days_logged_last_7) {
        return right.days_logged_last_7 - left.days_logged_last_7;
      }
      return right.days_logged_last_30 - left.days_logged_last_30;
    }

    if (sortBy === 'on_target') {
      const leftRatio = left.recent_hit_rate_pct == null
        ? -1
        : left.recent_hit_rate_pct;
      const rightRatio = right.recent_hit_rate_pct == null
        ? -1
        : right.recent_hit_rate_pct;
      if (rightRatio !== leftRatio) {
        return rightRatio - leftRatio;
      }
      return (right.days_on_target_last_7 ?? -1) - (left.days_on_target_last_7 ?? -1);
    }

    return right.meals_last_30 - left.meals_last_30;
  });

  return nextMembers;
};

const GroupDetail: React.FC = () => {
  const { groupId } = useParams<{ groupId: string }>();
  const navigate = useNavigate();
  const { logout, user: currentUser } = useAuth();

  const parsedGroupId = groupId ? parseInt(groupId, 10) : NaN;

  const [group, setGroup] = useState<AdminGroup | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState<GroupDetailSort>('last_active');
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const loadGroup = async () => {
      if (Number.isNaN(parsedGroupId)) {
        setError('Invalid group id.');
        setIsLoading(false);
        return;
      }

      setIsLoading(true);
      setError('');

      try {
        const response = await adminAPI.getGroup(parsedGroupId);
        if (response.success) {
          setGroup(response.group);
        } else {
          setError(response.error || 'Failed to load group');
        }
      } catch (err: any) {
        if (err.response?.status === 403) {
          setError('Only super admins can view groups.');
        } else if (err.response?.status === 404) {
          setError('Group not found.');
        } else {
          setError('Failed to load group');
        }
      } finally {
        setIsLoading(false);
      }
    };

    void loadGroup();
  }, [parsedGroupId]);

  const visibleMembers = useMemo(() => {
    if (!group) return [];

    const normalizedSearch = searchTerm.trim().toLowerCase();
    const filteredMembers = normalizedSearch
      ? group.members.filter((member) => member.email.toLowerCase().includes(normalizedSearch))
      : group.members;

    return sortMembers(filteredMembers, sortBy);
  }, [group, searchTerm, sortBy]);

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!group) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="text-center">
          <p className="text-red-600 mb-4">{error || 'Group not found.'}</p>
          <button
            onClick={() => navigate('/dashboard')}
            className="text-blue-600 hover:underline"
          >
            Back to Dashboard
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100">
      <header className="bg-white shadow">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex justify-between items-center">
          <div className="flex items-center space-x-4">
            <button
              onClick={() => navigate('/dashboard')}
              className="text-gray-600 hover:text-gray-900"
            >
              &larr; Back
            </button>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">{group.name}</h1>
              <p className="text-sm text-gray-500">
                {group.member_count} member{group.member_count === 1 ? '' : 's'} • Updated {formatDateTime(group.updated_at)}
              </p>
            </div>
          </div>
          <div className="flex items-center space-x-4">
            <button
              onClick={() => navigate(`/groups/${group.id}/edit`)}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm"
            >
              Edit Group
            </button>
            <span className="text-sm text-gray-600">{currentUser?.email}</span>
            <button
              onClick={logout}
              className="text-sm text-red-600 hover:text-red-800"
            >
              Logout
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-6 bg-blue-50 border border-blue-200 rounded-lg p-4">
          <p className="text-sm text-blue-800">
            Logging shows how many of the last 7 and 30 days each user actually entered data.
            Targets now show the last 5 calendar days. Each macro chip is delta vs target:
            negative means under, positive means over, and green means within 10%.
          </p>
        </div>

        {error && (
          <div className="mb-6 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
            {error}
          </div>
        )}

        <div className="bg-white rounded-lg shadow overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <h2 className="text-lg font-medium text-gray-900">Member Activity</h2>
              <p className="text-sm text-gray-500">Search the group or sort by activity and target adherence.</p>
            </div>
            <div className="flex flex-col sm:flex-row gap-3">
              <input
                type="text"
                value={searchTerm}
                onChange={(event) => setSearchTerm(event.target.value)}
                placeholder="Search by email..."
                className="w-full sm:w-64 px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              <select
                value={sortBy}
                onChange={(event) => setSortBy(event.target.value as GroupDetailSort)}
                className="px-4 py-2 border border-gray-300 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                {sortOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {visibleMembers.length === 0 ? (
            <div className="p-6 text-center text-gray-500">
              {group.members.length === 0 ? 'This group has no members yet.' : 'No group members match the current search.'}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      User
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Last Active
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Logging
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Targets (5d)
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Overall Activity
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {visibleMembers.map((member) => (
                    <tr
                      key={member.id}
                      className="hover:bg-gray-50 cursor-pointer"
                      onClick={() => navigate(`/users/${member.id}`)}
                    >
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-2">
                          <div className="text-sm font-medium text-gray-900">{member.email}</div>
                          {Boolean(member.is_trainer) && (
                            <span className="px-2 py-0.5 text-xs bg-indigo-100 text-indigo-700 rounded">
                              Trainer
                            </span>
                          )}
                          {(member.target_carbs == null || member.target_protein == null || member.target_fat == null) && (
                            <span className="px-2 py-0.5 text-xs bg-amber-100 text-amber-700 rounded">
                              No targets
                            </span>
                          )}
                        </div>
                        <div className="text-xs text-gray-500 mt-1">User ID {member.id}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                        <div>{formatDateTime(getLastActiveValue(member))}</div>
                        <div className="text-xs text-gray-500">Login {formatDateTime(member.last_login)}</div>
                        <div className="text-xs text-gray-500">Meal {formatDateTime(member.last_meal_logged)}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm">
                        <div className={getLoggingTone(member.days_logged_last_7)}>
                          {member.days_logged_last_7} / 7 days
                        </div>
                        <div className="text-xs text-gray-500">
                          {member.days_logged_last_30} / 30 days • {member.meals_last_30} meals
                        </div>
                      </td>
                      <td className="px-6 py-4 text-sm">
                        {!hasCompleteTargets(member) ? (
                          <>
                            <div className="text-gray-400">Targets missing</div>
                            <div className="text-xs text-gray-500">
                              Set targets before this column can score over or under.
                            </div>
                          </>
                        ) : member.days_logged_last_5 === 0 ? (
                          <div className="text-gray-400">No logged days in last 5</div>
                        ) : (
                          <>
                            <div className={getHitRateTone(member.recent_hit_rate_pct)}>
                              {member.recent_hit_rate_pct}% on target
                            </div>
                            <div className="text-xs text-gray-500">
                              {member.days_on_target_last_5} / {member.days_logged_last_5} logged days in range
                            </div>
                          </>
                        )}

                        <div className="mt-2 space-y-1">
                          {hasCompleteTargets(member) && member.recent_days.map((day) => (
                            <div key={day.date} className="flex items-start gap-2">
                              <div className="w-16 flex-shrink-0 text-xs text-gray-500 pt-0.5">
                                {formatShortDate(day.date)}
                              </div>
                              {day.logged ? (
                                <div className="flex flex-wrap gap-1">
                                  {macroSignals.map((macro) => (
                                    <span
                                      key={macro.key}
                                      className={`px-1.5 py-0.5 rounded text-xs font-medium ${getMacroSignalClasses(day.macros[macro.key])}`}
                                    >
                                      {macro.label} {formatMacroSignal(day.macros[macro.key])}
                                    </span>
                                  ))}
                                </div>
                              ) : (
                                <span className="text-xs text-gray-400">No log</span>
                              )}
                            </div>
                          ))}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                        <div>{member.total_meals} meals total</div>
                        <div className="text-xs text-gray-500">{member.active_days} active days</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <button
                          onClick={(event) => {
                            event.stopPropagation();
                            navigate(`/users/${member.id}`);
                          }}
                          className="text-blue-600 hover:text-blue-900"
                        >
                          View User
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default GroupDetail;
